<script setup>
/**
 * Mint a key. Two fields, and the second one is the whole reason this is a dialog.
 *
 * THE SENDER IS ASKED FOR EVERY TIME THERE IS ONE TO ASK ABOUT, not only when the server
 * would refuse. The sidecar answers `inbox_required` when a workspace has more than one
 * WhatsApp number (`resolveInbox`, `http/outbound-internal.ts`) and happily mints an
 * UNPINNED key when it has exactly one — and an unpinned key is a delayed fault, not a
 * convenience: it works perfectly until a second number is provisioned, at which point
 * the owner's booking form starts answering `ambiguous_sender` because of a change
 * somebody else made, on a different day, for an unrelated reason. So a workspace with
 * one sender gets that sender pre-chosen and stored, and a workspace with several must
 * choose. The only key this screen can create that is pinned to nothing is one created
 * before there was anything to pin it to.
 *
 * THE FORM SAYS WHAT THE SERVER WOULD SAY. `SENDER_REQUIRED` is the BFF's own sentence
 * for `inbox_required`, kept identical on purpose (see the note beside it in `i18n.js`),
 * so being refused locally and being refused upstream cannot become two different stories
 * about the same rule.
 *
 * "NO NUMBER CAN SEND" AND "WE COULD NOT READ THE NUMBERS" ARE DIFFERENT SENTENCES. The
 * first is a fact about the workspace and tells the owner to contact us; the second is a
 * failed request and tells her to try again. Collapsing them — which is what a bare empty
 * list would do — is how a page invents a state the workspace is not in.
 */
import { computed, ref, watch } from 'vue';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';

import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  /** `[{ value, label }]`, already translated — the page owns the join to inbox names. */
  senderOptions: { type: Array, default: () => [] },
  /** True when the senders READ failed, which is not the same as having none. */
  sendersUnavailable: { type: Boolean, default: false },
  isCreating: { type: Boolean, default: false },
  /** A form-level message from the last failed mint. */
  createError: { type: String, default: '' },
});

const emit = defineEmits(['create']);

const { t } = useWasloI18n();

const dialogRef = ref(null);
const label = ref('');
const inboxId = ref('');
const senderError = ref('');

const hasSenders = computed(() => props.senderOptions.length > 0);

/**
 * One number, so there is nothing to ask: it is chosen, and it is still SHOWN, because
 * "which number does this key send from" is a fact about the key the owner is about to
 * create and she should not have to infer it from the workspace having only one.
 */
const onlySender = computed(() =>
  props.senderOptions.length === 1 ? props.senderOptions[0].value : ''
);

watch(onlySender, only => {
  if (only && inboxId.value === '') inboxId.value = only;
});

/**
 * Opened blank every time. A label left over from the last key — "Booking form" — on a
 * key that is actually for something else is a name that will be trusted later, when the
 * question is which system to turn off.
 */
const open = () => {
  label.value = '';
  inboxId.value = onlySender.value;
  senderError.value = '';
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const create = () => {
  if (props.isCreating) return;
  senderError.value = '';

  if (hasSenders.value && inboxId.value === '') {
    senderError.value = t('WASLO.OUTBOUND.KEYS.NEW.SENDER_REQUIRED');
    return;
  }

  emit('create', {
    label: label.value.trim(),
    inboxId: inboxId.value === '' ? null : Number(inboxId.value),
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('WASLO.OUTBOUND.KEYS.NEW.TITLE')"
    :description="t('WASLO.OUTBOUND.KEYS.NEW.BODY')"
    :confirm-button-label="
      isCreating
        ? t('WASLO.OUTBOUND.KEYS.NEW.CREATING')
        : t('WASLO.OUTBOUND.KEYS.NEW.CREATE')
    "
    :cancel-button-label="t('WASLO.OUTBOUND.KEYS.NEW.CANCEL')"
    :is-loading="isCreating"
    :disable-confirm-button="isCreating || !hasSenders"
    data-waslo-outbound-key-dialog
    @confirm="create"
  >
    <div class="flex flex-col gap-4">
      <Banner v-if="createError" color="ruby" data-waslo-outbound-key-failure>
        {{ createError }}
      </Banner>

      <Banner
        v-if="sendersUnavailable"
        color="amber"
        data-waslo-outbound-key-senders-unavailable
      >
        {{ t('WASLO.OUTBOUND.COMMON.UNAVAILABLE') }}
      </Banner>
      <Banner
        v-else-if="!hasSenders"
        color="amber"
        data-waslo-outbound-key-no-senders
      >
        {{ t('WASLO.OUTBOUND.KEYS.NEW.SENDER_NONE') }}
      </Banner>

      <WasloInput
        v-model="label"
        :label="t('WASLO.OUTBOUND.KEYS.NEW.LABEL')"
        :placeholder="t('WASLO.OUTBOUND.KEYS.NEW.LABEL_PLACEHOLDER')"
        :hint="t('WASLO.OUTBOUND.KEYS.NEW.LABEL_HINT')"
        :disabled="isCreating"
        data-waslo-outbound-key-label-field
      />

      <WasloSelect
        v-if="hasSenders"
        v-model="inboxId"
        :label="t('WASLO.OUTBOUND.KEYS.NEW.SENDER')"
        :options="senderOptions"
        :placeholder="t('WASLO.OUTBOUND.KEYS.NEW.SENDER_PLACEHOLDER')"
        :disabled="isCreating"
        :message="senderError || t('WASLO.OUTBOUND.KEYS.NEW.SENDER_HINT')"
        :message-type="senderError ? 'error' : 'info'"
        data-waslo-outbound-key-sender-field
      />
    </div>
  </Dialog>
</template>
